import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
} from "reactstrap";
import { URL } from "../../api";
import axiosInstance from "../../Request";

const InstallmentPif = () => {
  const { cid, lid } = useParams();
  const [installments, setInstallmentsPif] = useState([]);

  // --- PAGINATION STATES ---
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  // --- PAYMENT LINK STATES ---
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEmi, setSelectedEmi] = useState(null);
  const [amount, setAmount] = useState("");
  const [paymentLink, setPaymentLink] = useState("");
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkError, setLinkError] = useState("");

  const userId = localStorage.getItem("user_id");

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      try {
        // V2: every loan's EMIs are keyed to its own loan (lead -> loan -> emi), so always filter by the
        // current lead's loan. The backend resolves lead_id -> loan_id, so switching loans in the header
        // shows only that loan's schedule. (The old payment/wallet customer-only path is obsolete.)
        const url = `${URL.INSTALLMENT_PIF}?customer_id=${cid}&loan_id=${lid}`;
        const response = await axiosInstance.get(url, { withCredentials: true });
        setInstallmentsPif(response.data || []);
        setCurrentPage(1);
      } catch (error) {
        console.error("Error fetching customer details:", error);
      }
    };
    fetchCustomerDetails();
  }, [cid, lid]);

  // --- PAGINATION LOGIC ---
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = installments.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(installments.length / rowsPerPage);

  // An EMI is unpaid (payable) when it still has outstanding and isn't marked Paid.
  const isPayable = (item) =>
    Number(item.outstanding_amount || 0) > 0 &&
    String(item.status_value || "").toLowerCase() !== "paid";

  const openPayModal = (emi) => {
    const totalAmount =
      Number(emi.outstanding_amount || 0) + Number(emi.penal_outstanding || 0);
    setSelectedEmi(emi);
    setAmount(totalAmount);
    setPaymentLink("");
    setLinkError("");
    setModalOpen(true);
  };

  const generatePaymentLink = async () => {
    setLinkError("");
    if (!userId) {
      setLinkError("User not identified — please log in again.");
      return;
    }
    if (!amount || Number(amount) <= 0) {
      setLinkError("Enter a valid amount.");
      return;
    }
    setLinkLoading(true);
    try {
      const response = await axiosInstance.get(
        `${URL.PAYMENT_LINK_GENERATE}?customer_id=${cid}&user_id=${userId}&amount=${amount}&flags=4`
      );
      const data = response.data || {};
      const pay = data.payInResponse || {};
      const link = pay.link || pay.url || pay.paymentLink || pay?.data?.link;
      if (link) {
        setPaymentLink(link);
      } else {
        setLinkError(data.error || pay.message || "Could not generate the payment link. Please try again.");
      }
    } catch (error) {
      console.error("Error generating payment link:", error);
      setLinkError(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Failed to generate the payment link."
      );
    } finally {
      setLinkLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(paymentLink);
    alert("Payment link copied!");
  };

  return (
    <div>
      <Table className="align-items-center table-flush" responsive>
        <thead className="thead-light">
          <tr>
            <th scope="col">Emi Number</th>
            <th scope="col">Amount</th>
            <th scope="col">Principal</th>
            <th scope="col">Interest</th>
            <th scope="col">Outstanding Amount</th>
            <th scope="col">Outstanding Principal</th>
            <th scope="col">Outstanding Interest</th>
            <th scope="col">Emi status</th>
            <th scope="col">Emi Date</th>
            <th scope="col">Action</th>
          </tr>
        </thead>
        <tbody>
          {currentRows.length > 0 ? (
            currentRows.map((item, index) => (
              <tr key={index}>
                <td>{item.emi_number}</td>
                <td>{item.emi_amount}</td>
                <td>{item.principal}</td>
                <td>{item.interest}</td>
                <td>{item.outstanding_amount}</td>
                <td>{item.outstanding_principal}</td>
                <td>{item.outstanding_interest}</td>
                <td>{item.status_value}</td>
                <td>{item.emi_date}</td>
                <td>
                  {/* Pay only for unpaid EMIs; hidden once the EMI is paid. */}
                  {isPayable(item) && (
                    <Button color="success" size="sm" onClick={() => openPayModal(item)}>
                      Pay
                    </Button>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="10" className="text-center">No installments found</td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* --- PAGINATION UI --- */}
      {totalPages > 1 && (
        <div
          className="d-flex justify-content-center align-items-center mt-3"
          style={{ gap: "15px" }}
        >
          <Button
            size="sm"
            color="outline-primary"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((prev) => prev - 1)}
          >
            Previous
          </Button>
          <span style={{ fontSize: "14px", fontWeight: "bold" }}>
            Page {currentPage} of {totalPages}
          </span>
          <Button
            size="sm"
            color="outline-primary"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((prev) => prev + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Payment Link Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
        <ModalHeader toggle={() => setModalOpen(false)}>
          Generate Payment Link
        </ModalHeader>
        <ModalBody>
          <Label>Emi Amount</Label>
          <Input
            type="text"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          {paymentLink && (
            <>
              <Label className="mt-3">Payment Link</Label>
              <Input value={paymentLink} disabled />
            </>
          )}
          {linkError && (
            <div className="text-danger mt-2" style={{ fontSize: "0.85rem" }}>
              {linkError}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {!paymentLink ? (
            <Button color="primary" onClick={generatePaymentLink} disabled={linkLoading}>
              {linkLoading ? "Generating…" : "Generate Link"}
            </Button>
          ) : (
            <Button color="secondary" onClick={copyLink}>
              Copy Link
            </Button>
          )}
          <Button color="danger" onClick={() => setModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default InstallmentPif;
